import type { CatalogEntry } from '../types';

/**
 * Notifications (feature `09`) error codes + catalog.
 *
 * Code shape is `09AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * Endpoint → `AA` map (NotificationsController):
 *   listNotifications=01, markAsRead=02, markAllRead=03, preferences=03.
 *
 * markAsRead throws (404 when the notification doesn't exist or isn't owned by
 * the caller); updatePreferences throws (400 on an unknown preference key in a
 * PUT row, on a row for a locked key, and on a decline threshold that is out of
 * range or sent for a key that has none). `NotificationProcessor` runs in the `api-notification` queue with no
 * HTTP caller and has no throw sites today; should it ever need to raise, use
 * `AppException` for consistent catalog-driven logging.
 */
export const NOTIFICATIONS_CODES = {
  /** listNotifications=01. */
  ListNotifications: {
    InvalidCursor: '090101',
  },
  /** markAsRead=02. */
  MarkAsRead: {
    NotificationNotFound: '090201',
  },
  /** preferences=03 (GET/PUT /notifications/preferences). */
  Preferences: {
    /** Name kept from the four-category matrix; it now means "unknown preference key". */
    InvalidCategory: '090301',
    PreferenceLocked: '090302',
    InvalidDeclineThreshold: '090303',
  },
} as const;

export const NOTIFICATIONS_CATALOG: Record<string, CatalogEntry> = {
  // ── listNotifications ──
  '090101': {
    httpStatus: 400,
    message: "That page cursor isn't valid. Omit it to start from the first page.",
  },
  // ── markAsRead ──
  '090201': { httpStatus: 404, message: "We couldn't find that notification." },
  // ── preferences ──
  '090301': {
    httpStatus: 400,
    message: "That notification setting isn't one we recognise.",
  },
  '090302': {
    httpStatus: 400,
    message: 'Billing notifications are always on and cannot be changed.',
  },
  '090303': {
    httpStatus: 400,
    message:
      'The deliverability threshold must be a whole number from 10 to 50, and only applies to "Deliverability declining".',
  },
};
